import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Handshake, Loader2, Tent } from 'lucide-react';
import { toast } from 'react-toastify';

import PublicLayout from '../layouts/PublicLayout';
import { useCheckPartnerUrl } from '../../services/partners';
import { useCreateEventLead } from '../../services/leads';
// Same fixed dark-grey brand theme as /referral and /partner-signup.
import {
  INPUT,
  LABEL,
  PAGE_BG,
  useBrandBodyBackground,
} from './../referral/brandTheme';
import VaGovAccountQuestion from '../referral/VaGovAccountQuestion';
import { formatUsPhone, isUsPhone, usPhoneDigits } from '../../lib/usPhone';
import MilitaryQuestions, {
  EMPTY_MILITARY,
  militaryAnswers,
} from '../reachOut/MilitaryQuestions';

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className={LABEL}>
      {label}
      {required ? <span className="text-rose-400"> *</span> : null}
    </label>
    {cloneElement(children, { id })}
  </div>
);

// QR/link landing for ALL partner types (2026-08-20 — formerly event-only;
// /event/:slug and /partner/:slug both render this, each the /r/:code
// redirect target for its partner type). A lightweight lead-capture form:
// name, phone, veteran / branch / rank and the VA.gov answer create a LEAD
// attributed to the partner, and our onboarding
// team calls them. Event leads park in the Event Leads list; other partners'
// leads ride the onboarding board's normal cadence. A secondary link still
// offers full account signup via the attributed registration.
const EventLanding = () => {
  const { slug, partnerReferralUrl } = useParams();
  const linkSlug = slug || partnerReferralUrl;
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [inactive, setInactive] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    vaGovAccount: '', // 'yes' | 'no' | 'unsure'
  });
  // Veteran + branch + rank, the same questions as the website form
  // (Nic 2026-10-14).
  const [military, setMilitary] = useState(EMPTY_MILITARY);

  useBrandBodyBackground();

  // Logs the 'visit' analytics event + returns the partner (or 410-inactive).
  const checkLink = useCheckPartnerUrl(
    (res) => {
      if (res?.partnerId && res.directSignup) {
        // API-feed partners (leads come strictly through their API) and the
        // referral-program umbrella never capture here — straight to
        // attributed registration, the old behavior for every partner link.
        navigate('/register', { replace: true, state: { partner: res } });
      } else if (res?.partnerId) {
        setPartner(res);
      } else {
        navigate('/register', { replace: true });
      }
    },
    {
      onError: (err) => {
        if (err?.response?.status === 410 && err.response.data?.inactive) {
          setInactive(true);
          return;
        }
        navigate('/register', { replace: true });
      },
    },
  );

  // Once-per-mount guard: StrictMode double-invokes effects in dev, which
  // would log two 'visit' events per load (same fix as PartnerLink).
  const checkedRef = useRef(false);
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    checkLink.mutate(linkSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLead = useCreateEventLead(() => setDone(true), {
    onError: (err) => {
      if (err?.response?.status === 410) {
        setInactive(true);
        return;
      }
      toast.error(
        err?.response?.data?.msg || 'Something went wrong. Please try again.',
      );
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) =>
    setForm((f) => ({ ...f, phone: formatUsPhone(e.target.value) }));
  const militaryState = militaryAnswers(military);
  const fieldsComplete =
    form.firstName.trim() &&
    form.lastName.trim() &&
    isUsPhone(form.phone) &&
    militaryState.complete &&
    form.vaGovAccount;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!fieldsComplete) {
      toast.error('Please complete all of the required fields.');
      return;
    }
    submitLead.mutate({
      slug: linkSlug,
      payload: {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: usPhoneDigits(form.phone),
        email: form.email.trim() || undefined,
        isVeteran: militaryState.isVeteran,
        rank: militaryState.rank,
        vaGovAccount: form.vaGovAccount,
      },
    });
  };

  return (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-md py-6">
        <div className="rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          {inactive ? (
            <div className="py-4 text-center">
              <h2 className="text-xl font-bold text-white">
                This link is no longer active
              </h2>
              <p className="mt-2 text-sm text-neutral-300">
                The link you followed has been retired. You can still start a
                claim with us directly.
              </p>
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="mt-5 rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-black hover:bg-neutral-200"
              >
                Continue to registration
              </button>
            </div>
          ) : done ? (
            <div className="py-4 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-400">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-white">You're all set!</h2>
              {/* "Stopping by at X" is venue wording — right for an event
                  booth, wrong next to a person or org name. */}
              <p className="mt-2 text-sm text-neutral-300">
                {partner?.partnerType === 'event'
                  ? `Thanks for stopping by${
                      partner?.partnerName ? ` at ${partner.partnerName}` : ''
                    }. Our team will reach out within 24-48 hours.`
                  : 'Thanks for reaching out. Our team will be in touch within 24-48 hours.'}
              </p>
            </div>
          ) : !partner ? (
            <div className="flex justify-center py-10 text-neutral-300">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-5 text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
                  {partner.partnerType === 'event' ? (
                    <Tent className="h-6 w-6 text-white" />
                  ) : (
                    <Handshake className="h-6 w-6 text-white" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-white">
                  Welcome from {partner.partnerName}!
                </h2>
                <p className="text-center text-sm text-neutral-300">
                  Leave your info and our team will reach out within 24-48 hours
                  to talk about your VA disability rating.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field id="event-firstName" label="First name" required>
                    <input
                      className={INPUT}
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                  <Field id="event-lastName" label="Last name" required>
                    <input
                      className={INPUT}
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                  <Field id="event-phone" label="Phone" required>
                    <input
                      className={INPUT}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={setPhone}
                      placeholder="(904) 555-1212"
                    />
                  </Field>
                  <Field id="event-email" label="Email (optional)">
                    <input
                      className={INPUT}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set('email')}
                    />
                  </Field>
                </div>

                <MilitaryQuestions value={military} onChange={setMilitary} />

                <VaGovAccountQuestion
                  value={form.vaGovAccount}
                  onChange={(v) => setForm((f) => ({ ...f, vaGovAccount: v }))}
                />

                <button
                  type="submit"
                  disabled={submitLead.isLoading}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:pointer-events-none disabled:opacity-50"
                >
                  {submitLead.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Have us reach out
                </button>

                <p className="text-center text-xs text-neutral-400">
                  Ready to dive in now?{' '}
                  <button
                    type="button"
                    // Straight to attributed registration with the partner we
                    // already checked — routing through /partner/:slug would
                    // re-check the link and double-log this person's 'visit'.
                    onClick={() =>
                      navigate('/register', { state: { partner } })
                    }
                    className="font-medium text-neutral-300 underline hover:text-white"
                  >
                    Create your account
                  </button>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default EventLanding;
